import React from 'react';
import { Button, Checkbox, Radio, Space, Tooltip, Tree } from 'antd';
import { message } from '../../utils/antdMessage';
import { listThirdPartySources } from '../../utils/thirdPartyMapSources';
import { buildMapKindTreeData } from '../../utils/orthoToolbox/mapKindTree';
import {
  allowedContourDisplayIntervals,
  bakedContourIntervalOf
} from '../../utils/mapLayerOverlay';

/**
 * 路网 / 等高线二级菜单（系统树 + 辅图层 + 透明度 + 第三方）
 * 等高线：每条成品后独立 5/10/20/30m，按间距抽稀；计曲线亮黄、间曲线浅白
 */
export default function OrthoMapKindMenu({
  kind,
  canMapLayerManage,
  mapPublishedLayers,
  mapOverlayActive,
  mapAuxChecked,
  mapOverlayOpacity,
  thirdPartyPanelOpen,
  contourDisplayIntervalById,
  onLayerContourIntervalChange,
  onOpenManage,
  onBeginBboxSelect,
  onCloseMenu,
  onTreeCheck,
  onLayerNameClick,
  onAuxCheck,
  onOpacityChange,
  onThirdPartyOpenChange
}) {
  const kindLabel = kind === 'contour' ? '等高线' : '路网';
  const layerById = {};
  (mapPublishedLayers || []).forEach((ly) => {
    layerById[String(ly.id)] = ly;
  });
  const checkedKeys = (mapPublishedLayers || [])
    .filter((ly) => ly.kind === kind && mapOverlayActive[ly.id])
    .map((ly) => String(ly.id));
  const thirdSources = listThirdPartySources(kind);
  const thirdOpen = !!thirdPartyPanelOpen[kind];

  const titleRender = (node) => {
    if (!node?.isLeaf || !/^\d+$/.test(String(node.key))) {
      return node.title;
    }
    const ly = layerById[String(node.key)];
    if (!ly) return node.title;
    const displayName = ly.name || node.title;
    const tip = String(ly.description || ly.styleHint?.description || '').trim();
    const flyHint = '点击名称定位到图层范围（或按后台设置飞设备）';
    const nameEl = (
      <span
        className="zhxt-contour-tree-name zhxt-contour-tree-name--clickable"
        role="button"
        tabIndex={0}
        title={tip ? `${tip}\n${flyHint}` : flyHint}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onLayerNameClick?.(ly);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onLayerNameClick?.(ly);
          }
        }}
      >
        {displayName}
      </span>
    );

    if (kind !== 'contour') {
      return tip ? (
        <Tooltip title={tip} placement="right">
          {nameEl}
        </Tooltip>
      ) : nameEl;
    }

    const baked = bakedContourIntervalOf(ly);
    const allowed = allowedContourDisplayIntervals(baked);
    const current = Number(contourDisplayIntervalById?.[ly.id]) || allowed[0] || baked;
    return (
      <div className="zhxt-contour-tree-row" onClick={(e) => e.stopPropagation()}>
        {tip ? (
          <Tooltip title={tip} placement="right">
            {nameEl}
          </Tooltip>
        ) : nameEl}
        <Radio.Group
          className="zhxt-contour-tree-interval"
          size="small"
          optionType="button"
          buttonStyle="solid"
          value={current}
          options={allowed.map((v) => ({ label: `${v}m`, value: v }))}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation?.();
            onLayerContourIntervalChange?.(ly, e.target.value);
          }}
        />
      </div>
    );
  };

  return (
    <div className="zhxt-map-kind-menu" onMouseDown={(e) => e.stopPropagation()}>
      <div className="zhxt-map-kind-menu-top">
        {canMapLayerManage ? (
          <Space size={4}>
            <Button size="small" type="link" onClick={() => onOpenManage(kind)}>
              管理
            </Button>
            <Button
              size="small"
              type="primary"
              ghost
              onClick={() => onOpenManage(kind, { createFocus: true })}
            >
              新建
            </Button>
          </Space>
        ) : (
          <span className="zhxt-ortho-muted">仅可勾选叠加</span>
        )}
        <Button
          size="small"
          type="link"
          onClick={() => {
            onBeginBboxSelect(kind === 'contour' ? 'map-contour' : 'map-road');
            onCloseMenu(kind);
          }}
        >
          框选加载
        </Button>
      </div>

      <Tree
        checkable
        defaultExpandAll
        selectable={false}
        treeData={buildMapKindTreeData(kind, mapPublishedLayers)}
        checkedKeys={checkedKeys}
        titleRender={titleRender}
        onCheck={(keys) => onTreeCheck(kind, keys)}
        style={{ maxHeight: 240, overflow: 'auto', marginTop: 4 }}
      />

      {kind === 'road' && (
        <div className="zhxt-map-kind-aux-row">
          <span className="zhxt-map-kind-aux-label">辅图层</span>
          <Checkbox
            className="zhxt-map-kind-aux"
            checked={!!mapAuxChecked.poi}
            onChange={(e) => onAuxCheck('poi', e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          >
            地标
          </Checkbox>
          <Checkbox
            className="zhxt-map-kind-aux"
            checked={!!mapAuxChecked.building}
            onChange={(e) => onAuxCheck('building', e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          >
            建筑
          </Checkbox>
        </div>
      )}

      <div className="zhxt-map-kind-menu-opacity">
        <span>透明度</span>
        <input
          type="range"
          min={0.2}
          max={1}
          step={0.05}
          value={mapOverlayOpacity}
          onChange={(e) => onOpacityChange(e.target.value)}
        />
        <span>{Math.round(mapOverlayOpacity * 100)}%</span>
      </div>

      <div className="zhxt-map-kind-third-block">
        <Checkbox
          className="zhxt-map-kind-third-toggle"
          checked={thirdOpen}
          onChange={(e) => onThirdPartyOpenChange(kind, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        >
          第三方{kindLabel}
        </Checkbox>
        {thirdOpen && (
          <div className="zhxt-map-kind-third-panel">
            {thirdSources.length === 0 ? (
              <div className="zhxt-ortho-muted" style={{ fontSize: 11 }}>
                暂无可用数据源，接入正式 API 后可在此框选加载。
              </div>
            ) : (
              thirdSources.map((src) => (
                <div key={src.id} className="zhxt-map-kind-third-item">
                  <span className="zhxt-map-kind-third-item-name" title={src.description || src.name}>
                    {src.name}
                  </span>
                  {src.bboxSupported !== false && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => {
                        message.info(`「${src.name}」框选加载待接入正式 API`);
                      }}
                    >
                      框选
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
